// NewCreateProgram.jsx - Refactored Main Component
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SideBar from "../../components/navigation/SideBar";
import StandardForm from "../../components/forms/StandardForm";
import { createProgram, clearPrograms, useCreateProgramMutation, addNewWorkoutToProgram, reorderWorkouts, removeNewWorkoutFromProgram, addNewExerciseToWorkouts, addNewExerciseToWorkout, removeNewExerciseFromWorkouts, updateProgramInfo, changeExerciseOrder, switchNewExercise, addNewToSuperset, removeNewFromSuperset, useCreateProgramDesignerMutation } from "../../store";
import { useCoach } from "../../hooks/useCoach";
import AddExerciseModal from "../../components/modals/AddExerciseModal";

// Import subcomponents
import WorkoutTypes from "../../components/program/WorkoutTypes";
import TypeContent from "../../components/program/TypeContent";
import ActionButtons from "../../components/program/ActionButtons";
import EmptyState from "../../components/program/EmptyState";

// Import utilities
import { generateAlphabetLabel, renameAllTabs } from "../../utils/programUtils";
import { nanoid } from "@reduxjs/toolkit";
import { programApi } from "../../store/apis/programApi";


function NewCreateProgram() {
  
  const PROGRAM_TYPES = [
    { id: 1, value: 'Bodybuilding', label: 'Bodybuilding' },
    { id: 2, value: 'Mobility', label: 'Mobility' },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { coach } = useCoach();
  
  // State management
  const [selectedType, setSelectedType] = useState(PROGRAM_TYPES[0]);
  const [programName, setProgramName] = useState('');
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [workouts, setWorkouts] = useState({});
  const [exercises, setExercises] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const { program } = useSelector((state) => { //estado que guarda el objeto grande
    return {program: state.program.data};
  });
  const [createProgramDesigner] = useCreateProgramDesignerMutation();
  // Tab management functions
  const addNewTab = () => {
    const newTab = {
      id: Date.now(),
      label: generateAlphabetLabel(tabs.length),
    };
    
    const updatedTabs = [...tabs, newTab];
    setTabs(updatedTabs);
    setActiveTab(newTab.id);
    
    setWorkouts(prev => ({ ...prev, [newTab.id]: [] }));
    setExercises(prev => ({ ...prev, [newTab.id]: [] }));
  };

  const removeTab = (tabId) => {
    const updatedTabs = tabs.filter(tab => tab.id !== tabId);
    const renamedTabs = renameAllTabs(updatedTabs);
    setTabs(renamedTabs);
    
    // Clean up related data
    const updatedWorkouts = { ...workouts };
    const updatedExercises = { ...exercises };
    delete updatedWorkouts[tabId];
    delete updatedExercises[tabId];
    setWorkouts(updatedWorkouts);
    setExercises(updatedExercises);
    
    // Update active tab if necessary
    if (activeTab === tabId) {
      setActiveTab(renamedTabs.length > 0 ? renamedTabs[0].id : null);
    }
  };

  // Workout management functions
  const addWorkout = () => {
    if (!activeTab) return;
    const currentTab = tabs.find(tab => tab.id === activeTab);
    const currentWorkouts = workouts[activeTab] || [];
    const type = currentTab.label;
    const orderInType = currentWorkouts.length + 1;
    const theoreticalOrder = type+orderInType;
    const newWorkout = {
      id: nanoid(),
      key: nanoid(),
      workoutName: programName+' '+theoreticalOrder || `Workout`+' '+theoreticalOrder,
      type: type,
      orderInType: orderInType,
      exercises: []
    };

    dispatch(addNewWorkoutToProgram(newWorkout)); 
    
    //check if there are exercises, if yes, place them in new workout
    const currentExercises = exercises[activeTab] || [];
    if(currentExercises.length>0){
      currentExercises.forEach((exercise, index)=> {
        dispatch(addNewExerciseToWorkout({ 
          key: exercise.key, 
          workoutKey: newWorkout.key, 
          exerciseId: exercise.exerciseId, 
          exerciseName: exercise.name 
        }));
      });
    }

    setWorkouts(prev => ({
      ...prev,
      [activeTab]: [...currentWorkouts, newWorkout]
    }));
  };

  const removeWorkout = (workoutId, workoutKey) => {
    if (!activeTab) return;
    const currentWorkouts = workouts[activeTab] || [];
    const updatedWorkouts = currentWorkouts.filter(workout => workout.id !== workoutId);
    dispatch(removeNewWorkoutFromProgram({workoutKey}));
    setWorkouts(prev => ({
      ...prev,
      [activeTab]: updatedWorkouts
    }));
  };

  // Exercise management functions
  const addExercise = () => {
    if (!activeTab) return;
    setOpenModal(true);
  };

  const handlePickExercise = (exercise) => {
    if (!activeTab) return;
    const currentTab = tabs.find(tab => tab.id === activeTab);
    const currentExercises = exercises[activeTab] || [];
    const newExercise = {
      id: nanoid(),
      key: nanoid(),
      exerciseId: exercise.id,
      name: exercise.name,
    };

    const type = currentTab.label;
    dispatch(addNewExerciseToWorkouts({
      key: newExercise.key,
      type:type, 
      exerciseId:exercise.id, 
      exerciseName: exercise.name,
    }));
    setExercises(prev => ({
      ...prev,
      [activeTab]: [...currentExercises, newExercise]
    }));
    
    //setOpenModal(false);
  };

  const removeExercise = (exerciseId, exerciseKey) => {
    if (!activeTab) return;
    
    const currentExercises = exercises[activeTab] || [];
    const updatedExercises = currentExercises.filter(exercise => exercise.id !== exerciseId);
    dispatch(removeNewExerciseFromWorkouts({exerciseKey}));
    setExercises(prev => ({
      ...prev,
      [activeTab]: updatedExercises
    }));
  };

  const onExerciseMoveUp = (exerciseId, exerciseKey) => {
    if (!activeTab) return;
    
    const currentExercises = exercises[activeTab] || [];
    const index = currentExercises.findIndex(ex => ex.id === exerciseId);
    
    if (index === -1 || index === 0) return;
    
    const exercise = currentExercises[index];
    
    // Si el ejercicio pertenece a un superset
    if (exercise.superset) {
      const supersetId = exercise.superset;
      
      // Encontrar el rango completo del superset actual
      let startIndex = index;
      let endIndex = index;
      
      while (startIndex > 0 && currentExercises[startIndex - 1].superset === supersetId) {
        startIndex--;
      }
      
      while (endIndex < currentExercises.length - 1 && currentExercises[endIndex + 1].superset === supersetId) {
        endIndex++;
      }
      
      if (startIndex === 0) return;
      
      // ✅ Verificar qué hay antes del bloque y saltar sobre otros bloques de superset
      let targetIndex = startIndex - 1;
      
      // Si el ejercicio anterior tiene un superset DIFERENTE, saltar sobre todo ese bloque
      if (currentExercises[targetIndex].superset && 
          currentExercises[targetIndex].superset !== supersetId) {
        const targetSupersetId = currentExercises[targetIndex].superset;
        
        // Buscar el inicio del bloque de superset anterior
        while (targetIndex > 0 && currentExercises[targetIndex - 1].superset === targetSupersetId) {
          targetIndex--;
        }
      }
      
      const updatedExercises = [...currentExercises];
      const supersetBlock = updatedExercises.splice(startIndex, endIndex - startIndex + 1);
      updatedExercises.splice(targetIndex, 0, ...supersetBlock);
      
      setExercises(prev => ({
        ...prev,
        [activeTab]: updatedExercises
      }));
      
      dispatch(changeExerciseOrder({
        exerciseKey: currentExercises[startIndex].key, 
        direction: 'up'
      }));
    } else {
      // Si NO está en superset, debe saltar sobre bloques completos de superset
      let targetIndex = index - 1;
      
      if (currentExercises[targetIndex].superset) {
        const targetSupersetId = currentExercises[targetIndex].superset;
        
        while (targetIndex > 0 && currentExercises[targetIndex - 1].superset === targetSupersetId) {
          targetIndex--;
        }
      }
      
      if (targetIndex < 0) return;
      
      const updatedExercises = [...currentExercises];
      const [movedExercise] = updatedExercises.splice(index, 1);
      updatedExercises.splice(targetIndex, 0, movedExercise);
      
      setExercises(prev => ({
        ...prev,
        [activeTab]: updatedExercises
      }));
      
      dispatch(changeExerciseOrder({exerciseKey, direction: 'up'}));
    }
  };

  const onExerciseMoveDown = (exerciseId, exerciseKey) => {
    if (!activeTab) return;
    
    const currentExercises = exercises[activeTab] || [];
    const index = currentExercises.findIndex(ex => ex.id === exerciseId);
    
    if (index === -1 || index === currentExercises.length - 1) return;
    
    const exercise = currentExercises[index];
    
    // Si el ejercicio pertenece a un superset
    if (exercise.superset) {
      const supersetId = exercise.superset;
      
      // Encontrar el rango completo del superset actual
      let startIndex = index;
      let endIndex = index;
      
      while (startIndex > 0 && currentExercises[startIndex - 1].superset === supersetId) {
        startIndex--;
      }
      
      while (endIndex < currentExercises.length - 1 && currentExercises[endIndex + 1].superset === supersetId) {
        endIndex++;
      }
      
      if (endIndex === currentExercises.length - 1) return;
      
      // ✅ Verificar qué hay después del bloque y saltar sobre otros bloques de superset
      let targetIndex = endIndex + 1;
      
      // Si el ejercicio siguiente tiene un superset DIFERENTE, saltar sobre todo ese bloque
      if (currentExercises[targetIndex].superset && 
          currentExercises[targetIndex].superset !== supersetId) {
        const targetSupersetId = currentExercises[targetIndex].superset;
        
        // Buscar el final del bloque de superset siguiente
        while (targetIndex < currentExercises.length - 1 && 
              currentExercises[targetIndex + 1].superset === targetSupersetId) {
          targetIndex++;
        }
      }
      
      const updatedExercises = [...currentExercises];
      const supersetBlock = updatedExercises.splice(startIndex, endIndex - startIndex + 1);
      updatedExercises.splice(targetIndex - (endIndex - startIndex), 0, ...supersetBlock);
      
      setExercises(prev => ({
        ...prev,
        [activeTab]: updatedExercises
      }));
      
      dispatch(changeExerciseOrder({
        exerciseKey: currentExercises[startIndex].key, 
        direction: 'down'
      }));
    } else {
      // Si NO está en superset, debe saltar sobre bloques completos de superset
      let targetIndex = index + 1;
      
      if (currentExercises[targetIndex].superset) {
        const targetSupersetId = currentExercises[targetIndex].superset;
        
        while (targetIndex < currentExercises.length - 1 && 
              currentExercises[targetIndex + 1].superset === targetSupersetId) {
          targetIndex++;
        }
      }
      
      if (targetIndex >= currentExercises.length) return;
      
      const updatedExercises = [...currentExercises];
      const [movedExercise] = updatedExercises.splice(index, 1);
      updatedExercises.splice(targetIndex, 0, movedExercise);
      
      setExercises(prev => ({
        ...prev,
        [activeTab]: updatedExercises
      }));
      
      dispatch(changeExerciseOrder({exerciseKey, direction: 'down'}));
    }
  };

  const onSwitchExercise = (newExercise, currentExercise) => {
    if (!activeTab) return;
    
    const currentTab = tabs.find(tab => tab.id === activeTab);
    const currentExercises = exercises[activeTab] || [];
    
    // Encontrar el índice del ejercicio actual
    const exerciseIndex = currentExercises.findIndex(ex => ex.key === currentExercise.key);
    
    // Si no se encuentra el ejercicio, salir
    if (exerciseIndex === -1) return;
    
    // Crear el nuevo ejercicio con la estructura correcta
    const replacementExercise = {
      id: nanoid(), // Nuevo ID local
      key: currentExercise.key, // Nueva key para Redux
      exerciseId: newExercise.id,
      name: newExercise.name,
      superset: currentExercise.superset
    };
    
    // Crear copia del array con el ejercicio reemplazado
    const updatedExercises = [...currentExercises];
    updatedExercises[exerciseIndex] = replacementExercise;
    
    // Actualizar estado local
    setExercises(prev => ({
      ...prev,
      [activeTab]: updatedExercises
    }));
    const type = currentTab.label;
    dispatch(switchNewExercise({type, currentExerciseKey: currentExercise.key, newExercise}));
    
  };

  const onClickSuperset = (exercise) => {
    if (!activeTab) return;
    const currentTab = tabs.find(tab => tab.id === activeTab);
    const currentExercises = exercises[activeTab] || [];
    
    // Encontrar el índice del ejercicio actual
    const currentIndex = currentExercises.findIndex(ex => ex.key === exercise.key);
    
    // Verificar que existe y que no es el último
    if (currentIndex === -1 || currentIndex >= currentExercises.length - 1) {
      console.log('No hay ejercicio siguiente');
      return;
    }
    
    // Obtener el ejercicio actual y el siguiente
    const currentExercise = currentExercises[currentIndex];
    const nextExercise = currentExercises[currentIndex + 1];
    
    // Determinar qué ID de superset usar (misma lógica que en Redux)
    let supersetId;
    
    if (currentExercise.superset) {
      supersetId = currentExercise.superset;
    } else if (nextExercise.superset) {
      supersetId = nextExercise.superset;
    } else {
      // ✅ Generar el ID aquí UNA SOLA VEZ
      supersetId = nanoid();
    }
    
    // Crear copia del array con los superset actualizados
    const updatedExercises = currentExercises.map((ex, index) => {
      if (index === currentIndex || index === currentIndex + 1) {
        return { ...ex, superset: supersetId };
      }
      return ex;
    });
    
    // Actualizar estado local
    setExercises(prev => ({
      ...prev,
      [activeTab]: updatedExercises
    }));
    
    // Dispatch a Redux pasando el supersetId generado
    const type = currentTab.label;
    dispatch(addNewToSuperset({ 
      exerciseKey: exercise.key, 
      type, 
      supersetId // ✅ Pasar el ID generado
    }));
  };

  const onRemoveExerciseFromSuperset = (exercise) => {
    if (!activeTab) return;
    
    const currentTab = tabs.find(tab => tab.id === activeTab);
    const currentExercises = exercises[activeTab] || [];
    
    // Encontrar el ejercicio objetivo
    const targetIndex = currentExercises.findIndex(ex => ex.key === exercise.key);
    if (targetIndex === -1) return;
    
    const targetExercise = currentExercises[targetIndex];
    if (!targetExercise.superset) return;
    
    const supersetId = targetExercise.superset;
    
    // Encontrar todos los ejercicios de la superserie (consecutivos)
    let startIndex = targetIndex;
    let endIndex = targetIndex;
    
    // Buscar hacia atrás
    while (startIndex > 0 && currentExercises[startIndex - 1].superset === supersetId) {
      startIndex--;
    }
    
    // Buscar hacia adelante
    while (endIndex < currentExercises.length - 1 && currentExercises[endIndex + 1].superset === supersetId) {
      endIndex++;
    }
    
    const supersetLength = endIndex - startIndex + 1;
    let updatedExercises = [...currentExercises];
    
    // CASO 1: Superserie de 2 ejercicios - quitar superset de ambos, mantener orden
    if (supersetLength === 2) {
      updatedExercises[startIndex] = { ...updatedExercises[startIndex], superset: null };
      updatedExercises[endIndex] = { ...updatedExercises[endIndex], superset: null };
    } 
    // CASO 2: Superserie de más de 2 ejercicios
    else {
      // Es el primer ejercicio de la superserie
      if (targetIndex === startIndex) {
        // Quitar superset del ejercicio clickeado
        updatedExercises[targetIndex] = { ...updatedExercises[targetIndex], superset: null };
        
        // Extraer el ejercicio
        const [removed] = updatedExercises.splice(targetIndex, 1);
        
        // El resto de la superserie (ahora en startIndex...endIndex-1) se mueve después del removido
        const remainingSupersetLength = supersetLength - 1;
        const supersetBlock = updatedExercises.splice(startIndex, remainingSupersetLength);
        
        // Insertar: primero el ejercicio removido, luego el bloque de superserie
        updatedExercises.splice(startIndex, 0, removed, ...supersetBlock);
      } 
      // No es el primer ejercicio de la superserie
      else {
        // Quitar superset del ejercicio clickeado
        updatedExercises[targetIndex] = { ...updatedExercises[targetIndex], superset: null };
        
        // Extraer el ejercicio
        const [removed] = updatedExercises.splice(targetIndex, 1);
        
        // Como removimos un elemento, endIndex cambió
        // Insertar el ejercicio después del último elemento de la superserie
        updatedExercises.splice(endIndex, 0, removed);
      }
    }
    
    // Actualizar estado local
    setExercises(prev => ({
      ...prev,
      [activeTab]: updatedExercises
    }));
    
    // Dispatch a Redux
    const type = currentTab.label;
    dispatch(removeNewFromSuperset({ exerciseKey:exercise.key, type }));
  };

  // Form configuration
  const meta = {
    object: "Program",
    action: "Create new Program",
    description: "Create a new macrocyle",
  };
  
  const config = [
    {
      id: 'name',
      label: 'Program name',
      type: 'shortText',
      placeholder: 'Macrocycle 1',
      size: 2,
      onChange: (e) => setProgramName(e.target.value),
    },
    {
      id: 'type',
      label: 'Type',
      type: 'dropdown',
      placeholder: 'Choose type',
      options: PROGRAM_TYPES,
      size: 2,
      onChange: setSelectedType,
    },
  ];
  
  const handleCreate = async (e) => {
    e.preventDefault();

    try {
      const data = await createProgramDesigner(program).unwrap();

      if (data?.success) {
        alert('¡Programa creado exitosamente!');
        dispatch(clearPrograms());
        // Invalidar la lista de programas en programApi para que /programs/ refetch
        dispatch(programApi.util.invalidateTags([{ type: 'Program', id: 'LIST' }]));
        //navigate(`/programs/${data.program_id}`);
        navigate("/programs/");
      } else {
        throw new Error(data?.error || 'Error desconocido');
      }

    } catch (error) {
      console.error('Error:', error);
      alert(`Error al crear el programa: ${error.message}`);
    }
  };
  
  // Inicializa el programa solo al montar el componente
  useEffect(() => {
    dispatch(clearPrograms());
    dispatch(createProgram({ name: '' ,type:selectedType.value, coach:coach.id}));
  }, []); // ✅ Array vacío - solo se ejecuta una vez

  // Actualiza solo el nombre cuando cambia
  useEffect(() => {
  if (programName) {
    dispatch(updateProgramInfo({programName})); // Necesitarás crear esta acción
  }
  }, [programName, dispatch]);

  return (
    <div>
      <SideBar />
      <main className="py-10 lg:pl-72">
        <div className="px-4 sm:px-6 lg:px-8">
          <StandardForm 
            meta={meta} 
            config={config} 
            colsForm={3} 
            onSubmit={handleCreate}
          />
        </div>
        
        <div className="mx-auto max-w-12xl px-4 py-2 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <div className="-mx-4 px-4 py-8 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg sm:px-8 sm:pb-14 lg:col-span-3 lg:row-span-2 lg:row-end-2 xl:px-2 xl:pb-10 xl:pt-8">
              
              <ActionButtons
                hasTabs={tabs.length > 0}
                onAddExercise={addExercise}
                onAddWorkout={addWorkout}
                onAddType={addNewTab}
              />
              
              <div className="mt-6">
                {tabs.length === 0 ? (
                  <EmptyState
                    title="No workout types. Click &quot;Add Type&quot; to start adding."
                    className="py-8"
                  />
                ) : (
                  <div>
                    <WorkoutTypes
                      tabs={tabs}
                      activeTab={activeTab}
                      onTabClick={setActiveTab}
                      onTabRemove={removeTab}
                    />
                    
                    <div className="py-6">
                      {tabs.map((tab) => (
                        <div
                          key={tab.id}
                          className={activeTab === tab.id ? 'block' : 'hidden'}
                        >
                          <TypeContent
                            tab={tab}
                            workouts={workouts[tab.id] || []}
                            exercises={exercises[tab.id] || []}
                            programName={programName}
                            onRemoveExercise={removeExercise}
                            onExerciseMoveUp={onExerciseMoveUp}
                            onExerciseMoveDown={onExerciseMoveDown}
                            onSwitchExercise={onSwitchExercise}
                            onClickSuperset={onClickSuperset}
                            onRemoveWorkout={removeWorkout}
                            onRemoveExerciseFromSuperset={onRemoveExerciseFromSuperset}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <AddExerciseModal 
        open={openModal} 
        setOpen={setOpenModal} 
        onAddExercise={handlePickExercise}
      />
    </div>
  );
}

function calculateOrder(workouts, type, orderInType) {
    // Obtener todos los tipos únicos (incluyendo el nuevo tipo si no existe)
    const existingTypes = new Set(workouts.map(w => w.type));
    existingTypes.add(type); // Añadir el tipo nuevo por si no existe
    
    // Ordenar alfabéticamente
    const uniqueTypes = [...existingTypes].sort();
    
    // Número total de tipos
    const numberOfTypes = uniqueTypes.length;
    
    // Posición del tipo (base 1)
    const typePosition = uniqueTypes.indexOf(type) + 1;
    
    // Calcular el order según la fórmula
    const order = (orderInType - 1) * numberOfTypes + typePosition;
    
    return order;
}

export default NewCreateProgram;